/**
 * The one pure transfer engine (Phase 2 contract). Every UI path — click,
 * keyboard, drag/drop — funnels through this function.
 *
 * Rules:
 *   - empty target: move the requested quantity;
 *   - same-item target: merge up to catalogue maxStack, remainder stays at source;
 *   - different-item target: full-stack moves swap, partial moves reject atomically;
 *   - anything invalid: return the containers unchanged with a typed reason.
 *
 * Invariants: no item creation or loss, no negative or zero-qty occupied
 * slots, no stack above maxStack.
 */
import {
  getSlot,
  isValidRef,
  maxStackOf,
  sameRef,
  withSlot,
  type Containers,
  type SlotRef,
} from './containers';

export type TransferFailureReason =
  | 'invalid-slot'
  | 'same-slot'
  | 'empty-source'
  | 'invalid-quantity'
  | 'target-full'
  | 'partial-into-occupied';

export type TransferResult =
  | { ok: true; moved: number; containers: Containers }
  | { ok: false; reason: TransferFailureReason; containers: Containers };

export function transfer(
  containers: Containers,
  from: SlotRef,
  to: SlotRef,
  qty: number,
): TransferResult {
  const fail = (reason: TransferFailureReason): TransferResult => ({ ok: false, reason, containers });

  if (!isValidRef(from) || !isValidRef(to)) return fail('invalid-slot');
  if (sameRef(from, to)) return fail('same-slot');

  const source = getSlot(containers, from);
  if (!source) return fail('empty-source');
  if (!Number.isInteger(qty) || qty < 1 || qty > source.qty) return fail('invalid-quantity');

  const target = getSlot(containers, to);

  if (target === null) {
    const remainder = source.qty - qty;
    const afterSource = withSlot(containers, from, remainder > 0 ? { itemId: source.itemId, qty: remainder } : null);
    return { ok: true, moved: qty, containers: withSlot(afterSource, to, { itemId: source.itemId, qty }) };
  }

  if (target.itemId === source.itemId) {
    const headroom = maxStackOf(source.itemId) - target.qty;
    if (headroom <= 0) return fail('target-full');
    const moved = Math.min(qty, headroom);
    const remainder = source.qty - moved;
    const afterSource = withSlot(containers, from, remainder > 0 ? { itemId: source.itemId, qty: remainder } : null);
    return {
      ok: true,
      moved,
      containers: withSlot(afterSource, to, { itemId: target.itemId, qty: target.qty + moved }),
    };
  }

  // Different item in the target: whole stacks swap; partial moves reject.
  if (qty !== source.qty) return fail('partial-into-occupied');
  const swappedSource = withSlot(containers, from, target);
  return { ok: true, moved: qty, containers: withSlot(swappedSource, to, source) };
}
